
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface PerformanceTabProps {
  data: {
    previousMatches: {
      passAccuracy: number[];
      possession: number[];
    };
    improvement: {
      passing: string;
      defensiveOrganization: string;
      pressSuccess: string;
    };
  };
}

const PerformanceTab: React.FC<PerformanceTabProps> = ({ data }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Performance Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <span className="font-medium">Pass Accuracy Trend:</span>
              <div className="mt-1 h-24 bg-gray-100 rounded p-2 flex items-end space-x-1">
                {data.previousMatches.passAccuracy.map((value, index) => (
                  <div
                    key={index}
                    className="bg-blue-500 w-full"
                    style={{ height: `${value}%` }}
                    title={`Match ${index + 1}: ${value}%`}
                  />
                ))}
              </div>
              <div className="mt-1 text-xs text-gray-500 text-center">
                Last 6 matches (oldest to newest)
              </div>
            </div>
            <Separator />
            <div>
              <span className="font-medium">Possession Trend:</span>
              <div className="mt-1 h-24 bg-gray-100 rounded p-2 flex items-end space-x-1">
                {data.previousMatches.possession.map((value, index) => (
                  <div
                    key={index}
                    className="bg-green-500 w-full"
                    style={{ height: `${value}%` }}
                    title={`Match ${index + 1}: ${value}%`}
                  />
                ))}
              </div>
              <div className="mt-1 text-xs text-gray-500 text-center">
                Last 6 matches (oldest to newest)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Performance Changes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <span className="font-medium">Improvement Areas:</span>
              <div className="mt-2 space-y-2">
                <div className={`p-2 rounded flex justify-between items-center ${data.improvement.passing.startsWith('+') ? 'bg-green-50' : 'bg-red-50'}`}>
                  <span>Passing</span>
                  <span className={data.improvement.passing.startsWith('+') ? 'text-green-600' : 'text-red-600'}>
                    {data.improvement.passing}
                  </span>
                </div>
                <div className={`p-2 rounded flex justify-between items-center ${data.improvement.defensiveOrganization.startsWith('+') ? 'bg-green-50' : 'bg-red-50'}`}>
                  <span>Defensive Organization</span>
                  <span className={data.improvement.defensiveOrganization.startsWith('+') ? 'text-green-600' : 'text-red-600'}>
                    {data.improvement.defensiveOrganization}
                  </span>
                </div>
                <div className={`p-2 rounded flex justify-between items-center ${data.improvement.pressSuccess.startsWith('+') ? 'bg-green-50' : 'bg-red-50'}`}>
                  <span>Press Success</span>
                  <span className={data.improvement.pressSuccess.startsWith('+') ? 'text-green-600' : 'text-red-600'}>
                    {data.improvement.pressSuccess}
                  </span>
                </div>
              </div>
            </div>
            <Separator />
            <div>
              <span className="font-medium">Overall Assessment:</span>
              <p className="mt-1 text-sm bg-blue-50 p-2 rounded">
                Based on the performance metrics, this session shows improvement in passing accuracy and defensive organization
                compared to previous sessions, but a slight decrease in pressing effectiveness.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceTab;
